import { ListChecksIcon } from 'lucide-react';
import React, { useCallback } from 'react';
import { toast } from 'sonner';

import { useEnsureInitialTasksMutation } from '@/services/api/tasksService';

import {
  type PaletteCommand,
  useRegisterCommands,
} from '../CommandPaletteContext';

const COMMAND_ID = 'ensure-initial-tasks';

/**
 * Registers the "Sync case tasks" command for the command palette.
 *
 * This hook is safe to call unconditionally; it internally guards against
 * missing `matterId` and returns an empty command list when not on a matter page.
 */
export const useEnsureInitialTasksCommand = (matterId?: string): void => {
  const [ensureInitialTasks] = useEnsureInitialTasksMutation();

  const createCommand = useCallback((): PaletteCommand[] => {
    if (!matterId) return [];

    return [
      {
        id: COMMAND_ID,
        label: 'Sync case tasks',
        description: 'Check for and create any missing required tasks',
        icon: React.createElement(ListChecksIcon, {
          className: 'h-4 w-4 text-gray-500 dark:text-zinc-400',
        }),
        group: 'Tasks',
        enabled: true,
        action: async () => {
          const loadingToast = toast.loading('Synchronizing tasks...', {
            description: 'Checking and creating missing tasks for this case',
          });

          try {
            await ensureInitialTasks({ matterId }).unwrap();
            toast.dismiss(loadingToast);
            toast.success('Tasks synchronized', {
              description: 'All required tasks are now set up for this case',
            });
          } catch (err) {
            console.error('Failed to sync tasks:', err);
            toast.dismiss(loadingToast);
            toast.error('Task sync failed', {
              description: 'Unable to synchronize tasks. Please try again.',
            });
            throw err;
          }
        },
      },
    ];
  }, [ensureInitialTasks, matterId]);

  useRegisterCommands(createCommand);
};
